import { api } from '@/shared/lib/api';

export interface BlogArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  metaTitle: string | null;
  metaDescription: string | null;
  keywords: string[];
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'PUBLISHED';
  generatedAt: string;
  sources: string[];
  reviewedBy: string | null;
  reviewedAt: string | null;
  publishedAt: string | null;
  views: number;
  createdAt: string;
  updatedAt: string;
  coverImage: string | null;
  author: string;
  readingTime: number | null;
  category: string | null;
  tags: string[];
}

export interface ListArticlesResponse {
  articles: BlogArticle[];
  total: number;
  limit: number;
  offset: number;
}

export interface UpdateArticleData {
  title?: string;
  content?: string;
  excerpt?: string;
  metaDescription?: string;
  keywords?: string[];
}

export const articlesService = {
  /**
   * Lista artigos com filtros
   */
  async listArticles(params?: {
    status?: BlogArticle['status'];
    limit?: number;
    offset?: number;
  }): Promise<ListArticlesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.offset) queryParams.append('offset', params.offset.toString());

    const response = await api.get<ListArticlesResponse>(
      `/blog/articles?${queryParams.toString()}`
    );
    return response.data;
  },

  /**
   * Busca um artigo por ID
   */
  async getArticle(id: string): Promise<BlogArticle> {
    const response = await api.get<BlogArticle>(`/blog/articles/${id}`);
    return response.data;
  },

  /**
   * Gera artigos manualmente
   */
  async generateArticles(): Promise<void> {
    await api.post('/blog/articles/generate');
  },

  /**
   * Aprova um artigo
   */
  async approveArticle(id: string, reviewedBy: string): Promise<BlogArticle> {
    const response = await api.patch<BlogArticle>(`/blog/articles/${id}/approve`, {
      reviewedBy,
    });
    return response.data;
  },

  /**
   * Rejeita um artigo
   */
  async rejectArticle(id: string, reviewedBy: string): Promise<BlogArticle> {
    const response = await api.patch<BlogArticle>(`/blog/articles/${id}/reject`, {
      reviewedBy,
    });
    return response.data;
  },

  /**
   * Publica um artigo
   */
  async publishArticle(id: string): Promise<BlogArticle> {
    const response = await api.patch<BlogArticle>(`/blog/articles/${id}/publish`);
    return response.data;
  },

  /**
   * Atualiza um artigo
   */
  async updateArticle(id: string, data: UpdateArticleData): Promise<BlogArticle> {
    const response = await api.put<BlogArticle>(`/blog/articles/${id}`, data);
    return response.data;
  },

  /**
   * Deleta um artigo
   */
  async deleteArticle(id: string): Promise<void> {
    await api.delete(`/blog/articles/${id}`);
  },

  /**
   * Regenera a imagem de capa de um artigo
   */
  async regenerateCoverImage(id: string): Promise<BlogArticle> {
    const response = await api.patch<BlogArticle>(`/blog/articles/${id}/regenerate-image`);
    return response.data;
  },
};
